import React, { useEffect, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { Loader2, Minus, Plus } from 'lucide-react';
import pdfWorker from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { cn } from '../ui/utils';

pdfjs.GlobalWorkerOptions.workerSrc = pdfWorker;

export const isPdfSource = (value, mimeType) => {
  if (mimeType && /pdf/i.test(mimeType)) return true;
  if (!value) return false;
  const source = String(value).split('#')[0];
  return (
    /\.pdf($|\?)/i.test(source) ||
    /application%2Fpdf/i.test(source) ||
    /type=application\/pdf/i.test(source)
  );
};

export const getPdfFileUrl = (value, mimeType) => {
  if (isPdfSource(value, mimeType)) return String(value);
  if (!value) return null;

  try {
    const url = new URL(String(value), 'https://example.com');
    const nested = url.searchParams.get('url') || url.searchParams.get('src');
    if (nested) {
      const decoded = decodeURIComponent(nested);
      if (isPdfSource(decoded)) return decoded;
    }
    if (/docs\.google\.com/i.test(url.hostname) && /\/(gview|viewer)/i.test(url.pathname) && nested) {
      return decodeURIComponent(nested);
    }
  } catch {
    return null;
  }

  return null;
};

export function PdfViewer({ fileUrl, variant = 'light', className }) {
  const containerRef = useRef(null);
  const [numPages, setNumPages] = useState(0);
  const [scale, setScale] = useState(1);
  const [pageWidth, setPageWidth] = useState(720);
  const [loadError, setLoadError] = useState('');
  const isDark = variant === 'dark';

  useEffect(() => {
    setNumPages(0);
    setLoadError('');
  }, [fileUrl]);

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return undefined;

    const updateWidth = () => {
      setPageWidth(Math.max(280, element.clientWidth - 48));
    };

    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  if (!fileUrl) return null;

  return (
    <div
      ref={containerRef}
      className={cn(
        'flex min-h-0 flex-col overflow-hidden',
        isDark ? 'h-full bg-[#1f2937]' : 'h-[480px] rounded-lg border border-border bg-muted/40',
        className
      )}
    >
      <div
        className={cn(
          'flex flex-shrink-0 items-center justify-center gap-3 border-b px-3 py-2 text-sm',
          isDark ? 'border-gray-700 text-gray-200' : 'border-border bg-white text-foreground'
        )}
      >
        <button
          type="button"
          onClick={() => setScale((value) => Math.max(0.6, Number((value - 0.15).toFixed(2))))}
          className={cn(
            'flex h-8 w-8 items-center justify-center rounded-md',
            isDark ? 'text-white hover:bg-white/10' : 'text-foreground hover:bg-muted'
          )}
          aria-label="Zoom out"
        >
          <Minus className="h-4 w-4" />
        </button>
        <span className="w-12 text-center text-xs tabular-nums">{Math.round(scale * 100)}%</span>
        <button
          type="button"
          onClick={() => setScale((value) => Math.min(2.4, Number((value + 0.15).toFixed(2))))}
          className={cn(
            'flex h-8 w-8 items-center justify-center rounded-md',
            isDark ? 'text-white hover:bg-white/10' : 'text-foreground hover:bg-muted'
          )}
          aria-label="Zoom in"
        >
          <Plus className="h-4 w-4" />
        </button>
        {numPages ? (
          <span className={cn('ml-2 text-xs', isDark ? 'text-gray-400' : 'text-muted-foreground')}>
            {numPages} {numPages === 1 ? 'page' : 'pages'}
          </span>
        ) : null}
      </div>

      <div className={cn('min-h-0 flex-1 overflow-auto py-4', isDark ? '' : 'bg-muted/30')}>
        <Document
          file={fileUrl}
          onLoadSuccess={({ numPages: nextNumPages }) => {
            setNumPages(nextNumPages);
            setLoadError('');
          }}
          onLoadError={() => setLoadError('Could not render this PDF.')}
          loading={
            <div
              className={cn(
                'flex h-full flex-col items-center justify-center gap-3 py-16',
                isDark ? 'text-gray-300' : 'text-muted-foreground'
              )}
            >
              <Loader2
                className={cn('h-8 w-8 animate-spin', isDark ? 'text-blue-400' : 'text-primary')}
              />
              <p className="text-sm">Loading pages...</p>
            </div>
          }
          error={
            <p
              className={cn(
                'px-6 py-16 text-center text-sm',
                isDark ? 'text-gray-300' : 'text-muted-foreground'
              )}
            >
              {loadError || 'Could not render this PDF.'}
            </p>
          }
        >
          {Array.from({ length: numPages }, (_, index) => (
            <Page
              key={`page-${index + 1}`}
              pageNumber={index + 1}
              width={pageWidth * scale}
              className="mx-auto mb-4 overflow-hidden rounded-sm bg-white shadow-lg"
              renderTextLayer
              renderAnnotationLayer
            />
          ))}
        </Document>
      </div>
    </div>
  );
}
